import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  Database,
  FormInput,
  Sparkles, 
  Heart, 
  Shirt,
  Users,
  Utensils
} from 'lucide-react'

export interface Solution {
  id: string
  title: string
  subtitle: string
  description: string
  longDescription: string
  icon: React.ElementType
  color: string
  bgColor: string
  features: string[]
  benefits: {
    title: string
    description: string
  }[]
  technologies: string[]
  image?: string
  screenshots?: string[]
}

// Static data that doesn't need translation
const staticSolutionData = {
  bares: {
    id: 'bares',
    icon: Utensils,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    technologies: ['React', 'TypeScript', 'Vite', 'Tailwind', 'Spring Boot 3', 'PostgreSQL', 'SSE', 'Docker']
  },
  erp: {
    id: 'erp',
    icon: Database,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    technologies: ['Java 21', 'Spring Boot 3', 'PostgreSQL', 'Vue 3', 'Quasar', 'Docker'],
    image: '/images/erp/1.principal.png'
  },
  forms: {
    id: 'forms',
    icon: FormInput,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    technologies: ['Vue 3', 'JSON Schema', 'TypeScript', 'Hot Reload', 'Custom Engine'],
    image: '/images/formstudio/1-main.png'
  },
  beauty: {
    id: 'beauty',
    icon: Sparkles,
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    technologies: ['Quasar', 'Vue 3', 'Spring Boot', 'OpenAI API', 'WhatsApp API', 'Capacitor'],
    image: '/images/salao_beleza.png'
  },
  petshop: {
    id: 'petshop',
    icon: Heart,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    technologies: ['Spring Boot', 'PostgreSQL', 'Quasar', 'Docker', 'RESTful API'],
    image: '/images/pet_shop.png'
  },
  fashion: {
    id: 'fashion',
    icon: Shirt,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    technologies: ['React', 'TypeScript', 'Tailwind', 'Instagram API', 'Microservices', 'Node.js'],
    image: '/images/loja_roupas.png'
  },
  workforce: {
    id: 'workforce',
    icon: Users,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    technologies: ['React', 'TypeScript', 'Spring Boot', 'Geolocation API', 'PWA', 'Real-time Sync']
  }
}

export const useSolutions = (): Solution[] => {
  const { t } = useTranslation('solutions')

  return useMemo(() => {
    const solutionIds = ['bares', 'erp', 'forms', 'beauty', 'petshop', 'fashion', 'workforce'] as const

    return solutionIds.map(id => {
      const staticData = staticSolutionData[id]
      
      return {
        ...staticData,
        title: t(`products.items.${id}.title`),
        subtitle: t(`products.items.${id}.subtitle`),
        description: t(`products.items.${id}.description`),
        longDescription: t(`products.items.${id}.longDescription`),
        features: t(`products.items.${id}.features`, { returnObjects: true }) as string[],
        benefits: t(`products.items.${id}.benefits`, { returnObjects: true }) as { title: string; description: string }[],
        technologies: [...staticData.technologies]
      }
    })
  }, [t])
}
